/* El criterio con el que se mira la documentación de un chofer.
 *
 * EL GEMELO ESTÁ EN FLOTABOT (`ficha.py` → `evaluar`), Y TIENE QUE DECIR LO
 * MISMO. Son dos aplicaciones con dos deploys, así que no puede haber un solo
 * archivo; lo que sí puede haber es una sola REGLA escrita dos veces y apareada
 * a propósito. Si tocás una, tocá la otra.
 *
 * Por qué importa más que de costumbre: el bot le avisa a documentación cuando
 * un camión vuelve a base, y esta pantalla es donde se resuelve ese aviso. Si
 * los dos criterios se separan, el mail dice que a alguien le falta el registro
 * y la pantalla lo muestra en verde — y ahí no se sabe a cuál creerle.
 *
 * EL CRITERIO ES EL DE LA OPERACIÓN, NO EL DE LA PLANILLA:
 *
 *   registro     lo necesita todo el mundo.
 *   peligrosas   SÓLO los marcados `(P)` en el nombre. Pedírselo a los 211
 *                convierte 2 vencimientos reales en 53, y una lista con 51
 *                avisos que no había que atender se deja de mirar.
 *   psicofísico  hace falta para RENOVAR el registro, no para salir a la ruta.
 *                Se muestra y no cuenta como faltante.
 *   curso        ya no se usa. No está y no va a estar.
 *
 * Y SIN FECHA NO ES AL DÍA. Es el hueco más accionable que hay: el documento
 * existe y nadie sabe hasta cuándo vale. Contarlo como "ok" es exactamente lo
 * que hace que alguien salga a la ruta con el registro vencido.
 *
 * ── LA PRÓRROGA DE PROVINCIA ────────────────────────────────────────────────
 *
 * Un chofer con zona en **provincia de Buenos Aires** tiene **60 días** desde
 * que se le vence el registro para renovarlo, y en el medio **puede salir**.
 * CABA no entra: es otra jurisdicción y emite su propio registro (ver
 * `shared/zona.js`).
 *
 * ES SU PROPIO ESTADO Y NO SE MEZCLA CON «EN CONDICIONES», que es la decisión
 * que sostiene todo esto. Las dos cosas dejan salir al camión, pero una está
 * resuelta y la otra tiene un reloj corriendo: fundidas, esos choferes
 * desaparecen de la lista de trabajo y el registro se renueva el día 61,
 * cuando ya no puede salir. Un vencimiento con fecha límite es exactamente lo
 * que esta pantalla existe para mostrar.
 *
 * Y tampoco es «no puede salir»: contarlo ahí frena a alguien que sí puede, y
 * el portón termina llamando por teléfono para destrabar algo que estaba bien.
 *
 * **Sin fecha de registro NO hay prórroga.** Son 60 días contados DESDE el
 * vencimiento, así que sin saber cuándo venció no hay de dónde contarlos —
 * darla igual sería inventar el plazo entero. Queda en «faltan», que es donde
 * ya estaba.
 *
 * **Y la prórroga es del REGISTRO, no de todo.** A un chofer marcado `(P)` con
 * las cargas peligrosas vencidas lo sigue frenando ese papel: son dos
 * documentos y el decreto habla de uno.
 *
 * ── EL QUE YA NO MANEJA NO CUENTA ───────────────────────────────────────────
 *
 * `activo = false` es «de baja». Sus vencimientos dejan de contar en todos los
 * números de esta pantalla y en las tarjetas de la home: si no maneja, su
 * registro vencido no frena ninguna salida, y contarlo infla justo el número
 * que decide si alguien mira la pantalla.
 *
 * **Y ESO NO SE HACE ADENTRO DE `evaluar`.** Un chofer de baja con el registro
 * vencido lo TIENE vencido, y `evaluar` tiene que seguir diciéndolo — es lo que
 * se lee al reactivarlo. Lo que cambia no es el veredicto sobre sus papeles
 * sino a quién se le pregunta: la baja filtra la LISTA, no el criterio.
 */

import { mediodiaHoyAr } from "./fechaAr";

import { nombreDe as nombreDeEmpresaRaw } from "./empresa";
import {
  SIN_DATO as TIPO_SIN_DATO,
  TIPOS,
  etiquetaTipo,
  tipoDe,
} from "./tipoServicio";
import { esBuenosAires } from "./zona";

export const DIAS_AVISO = 30;

/* `activo` es `not null default true`, así que sólo el `false` explícito es una
 * baja. Es a propósito que `undefined` NO lo sea: una consulta que se olvide de
 * pedir la columna dibujaría la tabla entera como dada de baja, y el error caro
 * de este par es esconder a alguien que sí maneja. */
export const estaDeBaja = (fila) => fila?.activo === false;

/* Los que siguen manejando. Es el universo de todos los conteos. */
export const soloActivos = (filas) =>
  (filas || []).filter((f) => !estaDeBaja(f));

export const soloDeBaja = (filas) => (filas || []).filter(estaDeBaja);

/* Los días que da la provincia. Vive acá y no en la pantalla porque lo leen
 * el contador, la fila y el gemelo de FlotaBot. */
export const DIAS_PRORROGA = 60;

/* La marca vive en el NOMBRE, que es donde la escribe quien mantiene la
 * planilla. Leerla de una lista aparte obligaría a mantener dos cosas
 * sincronizadas a mano. */
export const necesitaPeligrosas = (nombre) =>
  String(nombre || "").toUpperCase().includes("(P)");

export function diasHasta(fechaISO) {
  if (!fechaISO) return null;
  const s = String(fechaISO).slice(0, 10);
  const [a, m, d] = s.split("-").map(Number);
  if (!a || !m || !d) return null;
  const hoy = mediodiaHoyAr(); // el «hoy» argentino, no el de la máquina
  const cero = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((new Date(a, m - 1, d) - cero) / 86400000);
}

/* `estado` es `faltan` / `prorroga` / `avisa` / `ok`. Devuelve además las
 * frases, que son las que se muestran: un contador dice cuántos, la frase dice
 * qué hacer. */
export function evaluar(fila) {
  const pel = fila?.requiere_peligrosas ?? necesitaPeligrosas(fila?.nombre);
  const mirar = [["registro_vence", "registro"]];
  if (pel) mirar.push(["peligrosas_vence", "cargas peligrosas"]);

  const faltan = [];
  const avisa = [];
  let diasMin = null;
  let prorroga = null;

  for (const [campo, como] of mirar) {
    const dias = diasHasta(fila?.[campo]);
    if (dias === null) {
      faltan.push(`${como} sin fecha`);
    } else if (dias < 0) {
      const vencido = Math.abs(dias);
      /* El único documento con prórroga es el registro, y sólo en provincia.
       * `quedan` puede dar 0: ese día TODAVÍA puede salir, y mañana no. Por
       * eso el corte es `<= DIAS_PRORROGA` y la frase dice el número. */
      const quedan = DIAS_PRORROGA - vencido;
      if (campo === "registro_vence" && esBuenosAires(fila?.zona) && quedan >= 0) {
        prorroga = { vencidoHace: vencido, quedan, hasta: sumarDias(fila?.[campo], DIAS_PRORROGA) };
      } else {
        faltan.push(`${como} vencido hace ${vencido} días`);
      }
    } else if (dias <= DIAS_AVISO) {
      avisa.push(`${como} vence en ${dias} días`);
    }
    if (dias !== null && (diasMin === null || dias < diasMin)) diasMin = dias;
  }

  /* La prórroga va DELANTE de `avisa`: es la única de las dos con una fecha
   * después de la cual el camión no sale. */
  const estado = faltan.length ? "faltan"
    : prorroga ? "prorroga"
    : avisa.length ? "avisa" : "ok";

  return { estado, faltan, avisa, prorroga, peligrosas: pel, dias: diasMin };
}

/* La fecha hasta la que puede salir. Se muestra al lado de los días que
 * quedan: «quedan 19» obliga a hacer la cuenta para sacar un turno, y con la
 * fecha se agenda. */
export function sumarDias(fechaISO, dias) {
  if (!fechaISO) return null;
  const s = String(fechaISO).slice(0, 10);
  const [a, m, d] = s.split("-").map(Number);
  if (!a || !m || !d) return null;
  const f = new Date(a, m - 1, d + dias);
  const p = (n) => String(n).padStart(2, "0");
  return `${f.getFullYear()}-${p(f.getMonth() + 1)}-${p(f.getDate())}`;
}

/* El orden es una LISTA DE TRABAJO y no un padrón: primero lo que más urge.
 * Alfabético pondría arriba a los que están al día, que son la mayoría. */
export function ordenar(filas) {
  // La prórroga va entre las dos: no frena la salida, pero tiene fecha de
  // vencimiento — arriba de lo que sólo «avisa» y abajo de lo que no puede
  // salir hoy.
  const peso = { faltan: 0, prorroga: 1, avisa: 2, ok: 3 };
  return [...filas].sort((a, b) => {
    /* LOS DE BAJA VAN ÚLTIMOS, DESPUÉS DE «EN CONDICIONES». Casi siempre están
     * filtrados y no se ven; donde sí aparecen —una búsqueda por nombre— uno
     * con el registro vencido encabezaría la lista de trabajo, y es
     * exactamente el que no hay que atender. No se esconden: se corren. */
    const ba = estaDeBaja(a);
    const bb = estaDeBaja(b);
    if (ba !== bb) return ba ? 1 : -1;
    const ea = a._r ?? evaluar(a);
    const eb = b._r ?? evaluar(b);
    const pa = peso[ea.estado] ?? 3;
    const pb = peso[eb.estado] ?? 3;
    if (pa !== pb) return pa - pb;
    // Sin fecha va primero dentro de su grupo: es el hueco más accionable.
    const da = ea.dias ?? -99999;
    const db = eb.dias ?? -99999;
    if (da !== db) return da - db;
    return String(a.nombre || "").localeCompare(String(b.nombre || ""));
  });
}

/* ── Y ADEMÁS SE PUEDE ORDENAR POR CUALQUIER COLUMNA ────────────────────────
 *
 * Lo pidió Marcos: primero por chofer y por registro, y después por todas. El
 * orden de arriba —la lista de trabajo— sigue siendo el DEFAULT y se puede
 * volver a él, que es la parte que importa: es el único que contesta «qué hago
 * hoy», y un click no puede dejarte sin él para siempre. Por eso el ciclo tiene
 * TRES pasos y no dos: ascendente, descendente, y de vuelta al de urgencia.
 *
 * ── SIN DATO NO SE ORDENA COMO CERO, Y HAY DOS REGLAS ────────────────────
 *
 * Un chofer sin nombre no va entre la A y la B, y uno sin fecha de registro no
 * es «el 01/01/1970». Pero los dos huecos no valen lo mismo:
 *
 *   · en una FECHA, «sin fecha» es el hueco más accionable que hay, así que
 *     entra en el orden: en «más viejo primero» va ARRIBA, donde está lo
 *     urgente, y dado vuelta va abajo;
 *   · en cualquier otra columna va ÚLTIMO EN LAS DOS DIRECCIONES. Su lugar
 *     alfabético no existe, y meterlo en un extremo según la dirección lo
 *     escondería justo cuando alguien da vuelta el orden para encontrarlo.
 *
 * Un `null` tratado como texto vacío o como cero se ordena solo, y siempre
 * queda en un extremo sin que se note que está mal puesto.
 *
 * ── Y EL DESEMPATE SIEMPRE ES EL NOMBRE ──────────────────────────────────
 *
 * Sin él, ordenar por «Servicio» dejaría a los 200 de movimientos en el orden
 * en que vinieron de la base, que no es ninguno: la lista cambiaría sola entre
 * cargas sin que nadie tocara nada. Es la misma razón por la que el empate de
 * la barra de abajo del hub lo rompe el orden del catálogo.
 */

/* Cómo se lee cada columna. `texto` y `fecha` son los dos comportamientos de
 * arriba; están acá y no repartidos en `ordenarPor` para que agregar una
 * columna sea una línea y no una rama más adentro de un `sort`. */
const COLUMNAS = {
  chofer:     { tipo: "texto", de: (f) => f.nombre },
  registro:   { tipo: "fecha", de: (f) => f.registro_vence },
  /* SE ORDENA POR LO QUE SE VE. La celda dibuja «Transcan» y la columna guarda
   * un CUIT: ordenando por el crudo, la pantalla quedaría en un orden que no se
   * puede explicar mirándola — que es peor que no poder ordenar. Sin empresa
   * devuelve "" y no «Sin empresa», para que caiga en la regla del sin dato. */
  empresa:    { tipo: "texto",
                de: (f) => (String(f.empresa ?? "").trim()
                            ? nombreDeEmpresa(f.empresa) : "") },
  /* Y acá el alfabético NO sirve: «larga» iría antes que «movimientos» por
   * casualidad y el día que se renombre una etiqueta el orden cambiaría solo.
   * El orden es el del catálogo, que es estable. */
  servicio:   { tipo: "orden", de: (f) => posicionDeTipo(f.tipo_servicio) },
  /* LOS QUE NO LA NECESITAN NO SON «SIN FECHA». Son ~200 de 224, y contarlos
   * como hueco pondría toda la tabla arriba de los 2 vencimientos reales — que
   * es exactamente lo que `docs_que_avisan()` evita del lado del mail. */
  peligrosas: { tipo: "fecha",
                de: (f) => (esDePeligrosas(f) ? f.peligrosas_vence : undefined) },
  zona:       { tipo: "texto", de: (f) => f.zona },
  /* La dirección es la CALLE y la zona el PARTIDO: dos columnas y dos órdenes.
   * Sin esta entrada el encabezado sería un botón que no hace nada — y un
   * botón que no hace nada se aprieta tres veces y se deja de creer. */
  direccion:  { tipo: "texto", de: (f) => f.direccion },
  /* Se ordena por el número normalizado y no por lo tipeado: `011 15 6789` y
   * `11 6789` son la misma persona, y en crudo caen en lugares distintos. */
  telefono:   { tipo: "texto", de: (f) => soloDigitos(f.telefono) },
  /* Ordenar por una nota libre no ordena nada alfabéticamente útil; lo que sí
   * contesta es «¿quiénes tienen algo anotado?» — los sin nota van últimos. */
  anotaciones: { tipo: "texto", de: (f) => f.anotaciones },
  cargo:      { tipo: "texto", de: (f) => f.usuario_nombre || f.usuario },
};

export const COLUMNAS_ORDENABLES = Object.keys(COLUMNAS);

const soloDigitos = (v) => String(v ?? "").replace(/\D/g, "") || "";

/* LOS TRES SALEN DE DONDE YA VIVEN, y no de una copia acá.
 *
 * `nombreDe` es el mismo glosario con el que la celda dibuja la empresa, y
 * `TIPOS` es el mismo catálogo que llena el desplegable. Con una copia, el día
 * que se sume una empresa al glosario o se renombre un tipo, la tabla se
 * ordenaría por un vocabulario que ya no es el que muestra. Los tres módulos
 * viven en `shared/`, así que esto no arrastra nada de presentación. */
const nombreDeEmpresa = (v) => nombreDeEmpresaRaw(v);

/* La posición en el catálogo. `-1` es «no está», o sea sin dato, y la regla del
 * sin dato lo manda al final en las dos direcciones. */
function posicionDeTipo(v) {
  const t = tipoDe(v);
  if (t === TIPO_SIN_DATO) return -1;
  const i = TIPOS.findIndex((x) => x.clave === t);
  return i < 0 ? -1 : i;
}

/* El `(P)` del nombre, o la columna si está cargada. Es la misma pregunta que
 * hace `evaluar`, y por eso se le pasa la FILA y no el nombre. */
const esDePeligrosas = (f) =>
  f?.requiere_peligrosas ?? necesitaPeligrosas(f?.nombre);

const porNombre = (a, b) =>
  String(a.nombre || "").localeCompare(String(b.nombre || ""), "es",
                                       { sensitivity: "base" });

/* ── BUSCAR EN TODAS LAS COLUMNAS ─────────────────────────────────────────
 *
 * Lo pidió Marcos: la caja buscaba **sólo en nombre y DNI** —«las dos primeras
 * columnas de la izquierda»— así que de las nueve que se ven, siete no se
 * podían preguntar. Escribir «Transcan» o «MORÓN» contestaba *no hay
 * resultados* sobre una tabla que los tenía a la vista, y ése es el peor no
 * que puede dar un buscador: **quien busca ya sabe que eso está**, así que
 * concluye que el sistema no lo tiene.
 *
 * ── SE BUSCA EN LO QUE LA CELDA MUESTRA, igual que el orden ───────────────
 *
 * La columna guarda un CUIT y la celda dibuja «Transcan»: buscando el crudo,
 * la única forma de encontrar esa empresa sería escribir el CUIT, que nadie
 * tiene. Por eso pasa por el mismo glosario y el mismo catálogo que dibujan.
 *
 * **Y la SIGLA también** («TNSC»), que es lo que está escrito en la planilla:
 * el que va a corregirla busca por lo que va a leer allá, no por el nombre
 * lindo que traduce el hub.
 *
 * ── EL PIN NO SE BUSCA, Y ES A PROPÓSITO ─────────────────────────────────
 *
 * Es una credencial de pago y está detrás de un ojo justamente para no estar
 * en pantalla. Encontrar una fila tipeando un PIN haría dos cosas malas de una:
 * lo escribiría en una caja que queda en el historial del navegador, y
 * **confirmaría de quién es** — que es exactamente lo que el ojo evita. Es la
 * única columna visible que queda afuera, y por eso está dicho acá.
 *
 * ── VARIOS TÉRMINOS: TODOS TIENEN QUE APARECER, CADA UNO DONDE SEA ────────
 *
 * «juan transcan» encuentra al Juan de Transcan. Comparando la frase entera
 * contra cada columna no encontraría nada —no hay ninguna celda que diga las
 * dos cosas— y el que la escribió leería «no existe» de alguien que sí está.
 *
 * ── Y SIN ACENTOS ─────────────────────────────────────────────────────────
 *
 * «moron» tiene que encontrar MORÓN. Acá no es cosmético: las zonas venían con
 * los acentos rotos de la planilla (`MORÃN`, `CAÃUELAS`), y aunque ya se
 * repararon, una búsqueda que exige el acento correcto es una que falla justo
 * en la columna que decide la prórroga de 60 días.
 */

/* Minúsculas y sin tildes: `NFD` parte la letra de su acento y el rango borra
 * el acento. Se le pasa a los DOS lados —a lo tipeado y a lo guardado— así que
 * la comparación es simétrica y no importa cuál de los dos traiga el acento.
 *
 * La `ñ` también se aplana, y es deliberado: acá conviene errar hacia lo
 * permisivo. Buscando «canuelas» encuentra CAÑUELAS, y al revés también;
 * respetándola, el que no tiene la tecla a mano recibe un «no hay resultados»
 * sobre una fila que está a la vista. */
const plano = (v) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const fechaBuscable = (v) => {
  const iso = String(v ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [a, m, d] = iso.split("-");
  /* Las dos formas: la que se ve en la celda y la que está en la base. Alguien
   * que escribe «2026» o «03/2026» está preguntando por un mes de vencimiento,
   * y con una sola de las dos una de esas dos preguntas no contesta. */
  return `${iso} ${d}/${m}/${a} ${m}/${a}`;
};

/* Todo lo que una fila puede contestar, en un solo texto. Se arma una vez por
 * fila y por tecla; son 224 filas, así que no hay nada que optimizar — y un
 * `debounce` en una caja de búsqueda se lee como que el sistema se colgó. */
function textoDe(f) {
  const empresa = String(f.empresa ?? "").trim();
  return plano([
    f.nombre,
    f.dni,
    // El legajo no tiene columna propia pero está en el subtítulo del nombre.
    f.legajo,
    empresa,                                   // la sigla, tal cual la planilla
    empresa ? nombreDeEmpresa(empresa) : "",   // y el nombre que dibuja la celda
    etiquetaTipo(f.tipo_servicio),
    f.zona,
    f.direccion,
    f.telefono,
    soloDigitos(f.telefono),
    f.usuario_nombre,
    f.usuario,
    fechaBuscable(f.registro_vence),
    // Las peligrosas sólo son buscables en quien las necesita, por la misma
    // razón por la que sólo se ordenan ahí: en el resto la celda está vacía.
    esDePeligrosas(f) ? fechaBuscable(f.peligrosas_vence) : "",
    fechaBuscable(f.psicofisico_vence),
  ].join(" "));
}

/* `true` con la caja vacía: sin texto no hay pregunta, y filtrar por nada
 * dejaría la tabla vacía apenas se borra lo tipeado. */
export function coincide(fila, texto) {
  const terminos = plano(texto).split(/\s+/).filter(Boolean);
  if (terminos.length === 0) return true;
  const heno = textoDe(fila);
  return terminos.every((t) => heno.includes(t));
}

export function ordenarPor(filas, columna, desc = false) {
  const col = COLUMNAS[columna];
  if (!col) return ordenar(filas);
  const signo = desc ? -1 : 1;

  return [...filas].sort((a, b) => {
    const va = col.de(a);
    const vb = col.de(b);

    if (col.tipo === "fecha") {
      /* `undefined` es «no le hace falta» y `null`/vacío es «falta la fecha».
       * No son lo mismo: el primero no es un pendiente de nadie y va al final
       * siempre; el segundo es el hueco accionable y entra en el orden. */
      const na = va === undefined;
      const nb = vb === undefined;
      if (na !== nb) return na ? 1 : -1;
      if (na) return porNombre(a, b);

      const fa = va ? String(va).slice(0, 10) : null;
      const fb = vb ? String(vb).slice(0, 10) : null;
      if (fa === null || fb === null) {
        if (fa === fb) return porNombre(a, b);
        return (fa === null ? -1 : 1) * signo;
      }
      if (fa !== fb) return signo * (fa < fb ? -1 : 1);
      return porNombre(a, b);
    }

    if (col.tipo === "orden") {
      // Una posición de catálogo. `-1` es «no está» = sin dato, y va último en
      // las dos direcciones como cualquier otro hueco.
      const na = va < 0;
      const nb = vb < 0;
      if (na !== nb) return na ? 1 : -1;
      if (na) return porNombre(a, b);
      return va !== vb ? signo * (va - vb) : porNombre(a, b);
    }

    const ta = String(va ?? "").trim();
    const tb = String(vb ?? "").trim();
    // Sin dato SIEMPRE último, en las dos direcciones.
    if (!ta !== !tb) return ta ? -1 : 1;
    if (!ta) return porNombre(a, b);
    const cmp = ta.localeCompare(tb, "es", { sensitivity: "base" });
    return cmp !== 0 ? signo * cmp : porNombre(a, b);
  });
}
